import { useMemo } from 'react';
import { useGetCatalogQuery, useGetCatalogSongQuery } from '../../../../redux/api/fymAPI';
import { useQueryParam } from '../../../../utils/hooks/useQueryParam';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';
import { StudioSongFacts } from './details/props';
import { daysToRelease } from './studioRules';

/** While the lyrics sync and the realtime socket is down, the Studio reads the song again every 10 s. */
const SYNC_POLL_MS = 10_000;

/**
 * The song of the Studio (`?catalogItemUuid=`): the catalog row, its song files and the facts the
 * theme rules read (master, release date, lyrics, hook). A uuid that is not in the catalog gives
 * no item, so the Studio shows the song picker.
 */
export function useStudioSong(accountId: number) {
  const { canUseAiReels } = useMusic24Capabilities();
  const catalogItemUuid = useQueryParam('catalogItemUuid', undefined, '');
  const catalog = useGetCatalogQuery({ accountId });
  const item = useMemo(
    () => catalog.data?.items.find((candidate) => candidate.uuid === catalogItemUuid),
    [catalog.data, catalogItemUuid]
  );
  const { data: songFiles } = useGetCatalogSongQuery(
    { accountId, itemUuid: item?.uuid ?? '' },
    { skip: item === undefined }
  );
  // A second subscription that polls only while the lyrics sync and the socket is down
  // (`catalog.song.updated` refreshes the song while the socket is up).
  const isSyncing = (songFiles?.song.lyricTiming?.status ?? item?.song.lyricSyncStatus) === 'syncing';
  const pollingInterval = useRealtimePollInterval(SYNC_POLL_MS);
  useGetCatalogSongQuery(
    { accountId, itemUuid: item?.uuid ?? '' },
    { skip: item === undefined || !isSyncing, pollingInterval }
  );

  const song = useMemo<StudioSongFacts>(
    () => ({
      masterReady: item?.song.masterStatus === 'ready',
      daysToRelease: daysToRelease(item?.releaseDate ?? null, new Date()),
      canUseAiReels,
      hasLyrics: item?.song.hasLyrics === true,
      lyrics: songFiles?.song.lyrics ?? null,
      hookStartSec: songFiles?.song.master?.hookStartSec ?? null,
      durationSec: songFiles?.song.master?.durationSec ?? item?.song.durationSec ?? null,
      lyricSyncState: songFiles?.song.lyricTiming?.status ?? item?.song.lyricSyncStatus ?? 'none',
      lyricTiming: songFiles?.song.lyricTiming ?? null,
    }),
    [canUseAiReels, item, songFiles]
  );

  return { catalog, item, song };
}
